import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useLazyLoadQuery } from "react-relay";
import { Navigate } from "react-router";
import { HeaderLoader } from "../../../components/HeaderLoading";
import { Header } from "../../../components/Header";
import {
  useViewer,
  useViewerPermission,
} from "../../../app-controller/ViewerContext";
import { TimeTracksAllQuery } from "./__generated__/TimeTracksAllQuery.graphql";
import { MaybeAllowed } from "../../../app-controller/ViewerUtils";
import { formatISO, subDays } from "date-fns";
import { TimeTrackHeaderTabs } from "./TimeTrackHeaderTabs";

const TimeTrackWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "TimeTrackWorkspace" */ "./TimeTrackWorkspaceWrapper"
    ),
);

const queryDocument = graphql`
  query TimeTracksAllQuery($recordedStartAfter: DateTime) {
    viewer {
      ...TimeTrackWorkspace_user
    }

    ...TimeTrackWorkspace_query
      @arguments(
        onlyCreatedByOrAssignedUser: null
        onlyUnapproved: false
        recordedStartAfter: $recordedStartAfter
      )
  }
`;

export function TimeTracksAll() {
  const viewer = useViewer();
  const viewerUsername = viewer?.username ?? "me";
  const canViewFullTimeTrackList = useViewerPermission(
    "canViewFullTimeTrackList",
  );
  const canViewLastTwoWeeksTimeTrackList = useViewerPermission(
    "canViewLastTwoWeeksTimeTrackList",
  );

  if (
    canViewFullTimeTrackList === MaybeAllowed.NO &&
    canViewLastTwoWeeksTimeTrackList === MaybeAllowed.NO
  ) {
    return <Navigate to={`/user/${viewerUsername}/workspace`} replace />;
  }

  return (
    <TimeTracksAllContent
      canViewLastTwoWeeksTimeTrackList={canViewLastTwoWeeksTimeTrackList}
    />
  );
}

function TimeTracksAllContent({
  canViewLastTwoWeeksTimeTrackList,
}: {
  canViewLastTwoWeeksTimeTrackList: MaybeAllowed;
}) {
  const [recordedStartAfter, setRecordedStartAfter] = useState(() =>
    canViewLastTwoWeeksTimeTrackList === MaybeAllowed.PROBABLY_YES ||
    canViewLastTwoWeeksTimeTrackList === MaybeAllowed.YES
      ? formatISO(subDays(new Date(), 14))
      : null,
  );
  useEffect(() => {
    setRecordedStartAfter(
      canViewLastTwoWeeksTimeTrackList === MaybeAllowed.PROBABLY_YES ||
        canViewLastTwoWeeksTimeTrackList === MaybeAllowed.YES
        ? formatISO(subDays(new Date(), 14))
        : null,
    );
  }, [canViewLastTwoWeeksTimeTrackList]);

  const variables = useMemo(
    () => ({
      recordedStartAfter,
    }),
    [recordedStartAfter],
  );

  const props = useLazyLoadQuery<TimeTracksAllQuery>(queryDocument, variables, {
    fetchPolicy: "store-and-network",
  });

  if (!props.viewer) {
    return <Navigate to="/user/" replace />;
  }

  return (
    <>
      <Header
        title="Alle Mitarbeiter"
        subTitle="Workspace"
        isLoading={false}
        bigContainer
      >
        <TimeTrackHeaderTabs />
      </Header>
      <Suspense fallback={<HeaderLoader />}>
        <TimeTrackWorkspace showDownload user={props.viewer} query={props} />
      </Suspense>
    </>
  );
}
